import intersection from 'lodash-es/intersection';

import type { WorkbookLayerDto } from 'cadenza/workbook/api/workbook-layer-dto';
import type { WorkbookLayerTreeNode } from 'cadenza/workbook/api/workbook-layer-tree-node';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import type { ViewMetadata, WorkbookState } from 'cadenza/workbook/workbook.state';
import {
  getCouplingLayerLimit,
  getViewById,
  getViewMetadata,
  getViews,
  removeZoomRelatedViewId,
  setTargetMapExtentAndScaleForCoupledView,
  synchronizeToLeftTopMostView,
  slice,
  stopInteractionForCoupledView
} from 'cadenza/workbook/workbook.state';
import type { MapCouplingGroupDto, MapCouplingGroupId } from 'cadenza/workbook/model/map-coupling-group-dto';
import { store } from 'cadenza/store/cadenza-store';
import type { ExecuteCommandResult } from 'cadenza/api-client/workbook-api';
import { colStart, rowStart } from 'cadenza/workbook/worksheet/dto/grid-area-dto';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import { keys } from 'cadenza/utils/type-util';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { validateMapViewLayers } from 'cadenza/workbook/view-validation/view-validation';
import { assert } from 'cadenza/utils/custom-error';

import type { PayloadAction, SliceCaseReducers } from '@reduxjs/toolkit';
import type { Extent } from 'ol/extent';

export interface MapViewMetadata extends ViewMetadata {
  mapExtent?: WorldBoxDto;
  mapScale?: number;
  visibleRangeByLayerId?: Record<LayerId, boolean>;
}

export interface MapViewWorkbookState {
  /**
   * Holds configuration and transient state of map coupling groups (map views that try
   * to synchronize either scale or extent).
   * Coupled map views reference their group id.
   */
  mapCouplingGroupsById: Record<MapCouplingGroupId, MapCouplingGroupWithState>;
  zoomRelatedViewIds?: ViewId[];
}

/**
 * Extends map coupling group by a transient state of current target scale/extent.
 */
export interface MapCouplingGroupWithState extends MapCouplingGroupDto {
  /** Target scale for coupled views */
  extent?: Extent;
  /** Target extent for coupled views */
  scale?: number;
  /** View that set the new target values. Used to avoid feedback loops of changed loop */
  initiator?: ViewId;
  animatingOrInteracting?: boolean;
  isLayerLimitExceeded: boolean;
}

export function createInitialMapViewState () {
  return {
    mapCouplingGroupsById: {}
  };
}

export function patchZoomRelatedViewsState (state: WorkbookState, result: ExecuteCommandResult) {
  state.zoomRelatedViewIds = intersection(result.viewsToZoomToDataExtent, keys(state.viewById));
}

export function patchMapViewState (state: WorkbookState, result: ExecuteCommandResult) {
  patchZoomRelatedViewsState(state, result);
  validateMapViewLayers(state);
}

export const mapViewReducers = {
  setTargetMapExtentAndScaleForCoupledView (
    state: WorkbookState,
    { payload: { viewId, extent, scale, animatingOrInteracting = false, isLayerLimitExceeded = false } }:
    PayloadAction<{ viewId: ViewId; extent: WorldBoxDto; scale: number; animatingOrInteracting?: boolean; isLayerLimitExceeded?: boolean }>) {
    const group = getMapCouplingGroupWithState(viewId, state);
    assert(group, 'Map view not part of coupling group');

    Object.assign(group, {
      scale,
      extent,
      initiator: viewId,
      animatingOrInteracting,
      isLayerLimitExceeded
    });
  },
  stopInteractionForCoupledView (
    state: WorkbookState,
    { payload: { viewId } }: PayloadAction<{ viewId: ViewId }>) {
    const group = getMapCouplingGroupWithState(viewId, state);
    assert(group, 'Map view not part of coupling group');

    group.animatingOrInteracting = false;
  },
  synchronizeToLeftTopMostView (state: WorkbookState, { payload: { groupId } }: PayloadAction<{ groupId: MapCouplingGroupId }>) {
    const mostLeftTopView = getMostLeftTopViewOfGroup(groupId, state);
    const metadata = getViewMetadata<MapViewMetadata>(state, mostLeftTopView.id)!;
    const group = getMapCouplingGroupWithStateById(groupId, state);

    Object.assign(group, {
      initiator: mostLeftTopView.id,
      scale: metadata.mapScale!,
      extent: metadata.mapExtent!,
      isLayerLimitExceeded: false
    });
  },
  removeZoomRelatedViewId (state, { payload }: PayloadAction<ViewId>) {
    state.zoomRelatedViewIds = getZoomRelatedViewIds(state).filter(id => id !== payload);
  }
} satisfies SliceCaseReducers<WorkbookState>;

export { setTargetMapExtentAndScaleForCoupledView, stopInteractionForCoupledView, synchronizeToLeftTopMostView, removeZoomRelatedViewId };

export function getMapCouplingGroupWithStateById (couplingGroupId: MapCouplingGroupId, state = store.getState()) {
  return (slice(state)).mapCouplingGroupsById[couplingGroupId];
}

export function getMapCouplingGroupWithState (viewId: ViewId, state = store.getState()) {
  // ?. because view might already be removed from state when executing this in an observer
  const groupId = getViewById(state, viewId)?.mapViewContext?.mapCouplingGroupId;
  return groupId
    ? getMapCouplingGroupWithStateById(groupId, state)
    : undefined;
}

export function getMostLeftTopViewOfGroup (groupId: MapCouplingGroupId, state: WorkbookState = store.getState()) {
  const views = getViewsOfMapCouplingGroup(groupId, state);
  return getMostLeftTopView(views);
}

export function getViewsOfMapCouplingGroup (groupId: MapCouplingGroupId, state: WorkbookState = store.getState()) {
  return getViews(state)
    .filter(view => view.mapViewContext?.mapCouplingGroupId === groupId);
}

function getMostLeftTopView (views: ViewDto[]) {
  return views
    .reduce((currentMostLeftTopView, candidateView) => {
      const currentMostLeftTopViewsGridArea = currentMostLeftTopView.layout;
      const candidateViewsGridArea = candidateView.layout;

      const isMoreLeft = colStart(candidateViewsGridArea) < colStart(currentMostLeftTopViewsGridArea);
      const isEqualLeft = colStart(candidateViewsGridArea) === colStart(currentMostLeftTopViewsGridArea);
      const isMoreTop = rowStart(candidateViewsGridArea) < rowStart(currentMostLeftTopViewsGridArea);

      return isMoreLeft || (isEqualLeft && isMoreTop)
        ? candidateView
        : currentMostLeftTopView;
    });
}

export function getZoomRelatedViewIds (state: WorkbookState = store.getState()) {
  return slice(state).zoomRelatedViewIds ?? [];
}

export const getMapViewSrs = (state: WorkbookState, viewId: ViewId) =>
  getViewById(state, viewId)?.mapViewContext?.projection.code;

export const isMapLayerInVisibleRange = (state: WorkbookState, viewId: ViewId, layerId: LayerId) =>
  getViewMetadata<MapViewMetadata>(state, viewId)?.visibleRangeByLayerId?.[layerId] ?? true;

export const isCouplingLayerLimitExceededForGroupByViewId = (viewId: ViewId, state: WorkbookState = store.getState()) => {
  const mapCouplingGroupId = getViewById(state, viewId)?.mapViewContext?.mapCouplingGroupId;
  return mapCouplingGroupId && isCouplingLayerLimitExceededForGroup(mapCouplingGroupId, state);
};

export const isCouplingLayerLimitExceededForGroup = (groupId: MapCouplingGroupId, state: WorkbookState = store.getState()) => {
  const groupLayerCount = getCouplingLimitRelevantLayerCountOfGroup(groupId, state);
  const couplingLayerLimit = getCouplingLayerLimit(state);
  return groupLayerCount > couplingLayerLimit;
};

/**
 * Returns the count of layers of the given map view, relevant for coupling of map views.
 * If already coupled, it will return the layer count for the group, of only the view otherwise.
 */
export function getCouplingLimitRelevantLayerCountOfViewOrGroup (viewId: ViewId, state: WorkbookState = store.getState()) {
  const mapCouplingGroupId = getViewById(state, viewId)?.mapViewContext?.mapCouplingGroupId;
  return mapCouplingGroupId
    ? getCouplingLimitRelevantLayerCountOfGroup(mapCouplingGroupId, state)
    : getCouplingLimitRelevantLayerCountOfView(getViewById(state, viewId));
}

export function getCouplingLimitRelevantLayerCountOfGroup (groupId: MapCouplingGroupId, state: WorkbookState = store.getState()) {
  return getViewsOfMapCouplingGroup(groupId, state)
    .map(view => getCouplingLimitRelevantLayerCountOfView(view))
    .reduce((sum, layerCount) =>  sum + layerCount, 0);
}

/**
 * 1. Far all layers except group layers in map view the sum is increased by one (independent of layer visibility)
 * 2. For standard group layers the sum is increased by calculating the sum of all nested layers by using rules 1,2 and 3 (independent of layer visibility)
 * 3. For toggle layers the sum is increased by the maximum sum of all nested layers by using rules 1,2,3 (independent of layer visibility)
 *
 * @param view - view, whose layers will be counted
 */
export function getCouplingLimitRelevantLayerCountOfView (view: ViewDto) {
  const layerTree = view.layerTree;
  const layerDefinitions = view.layerDefinitions;
  return couplingLimitRelevantLayerCountOfView(layerTree, layerDefinitions);
}

function couplingLimitRelevantLayerCountOfView (
  layerTree: WorkbookLayerTreeNode[],
  layerDefinitions: Record<LayerId, WorkbookLayerDto>,
  isChildOfToggleGroup: boolean = false): number {
  return layerTree
    .reduce((sum, layer) => {
      if (layerDefinitions[layer.id].structureType === 'GROUP') {
        return sum + couplingLimitRelevantLayerCountOfView(layer.children, layerDefinitions);
      }
      if (layerDefinitions[layer.id].structureType === 'TOGGLE') {
        return sum + layer.children.reduce((max, child) => {
          if (child.children.length) {
            const count = couplingLimitRelevantLayerCountOfView(
              child.children,
              layerDefinitions,
              layerDefinitions[child.id].structureType === 'TOGGLE');
            return Math.max(max, count);
          }
          return max;
        }, 1);
      }
      return isChildOfToggleGroup ? Math.max(sum, 1) : sum + 1;
    }, 0);
}
